import { useEffect, useState } from 'react';

import { pricingRulesAPI } from '../lib/api';

/**
 * Le prix au litre que les barèmes de la société proposent.
 *
 * Six champs de prix, dans cinq écrans, redemandaient à l'exploitant ce que la
 * base savait déjà. La table `pricing_rules`, son écran de saisie et la
 * promesse « barèmes appliqués automatiquement » existaient tous — mais aucun
 * formulaire n'appelait la résolution, et la création d'un trajet allait
 * jusqu'à écrire `725` en dur.
 *
 * **Le barème propose, il n'impose pas.** Dès que l'exploitant touche au champ,
 * le hook cesse de le renseigner: un prix négocié l'emporte toujours sur un
 * tarif de référence, et un écran qui réécrit par-dessus la frappe est un écran
 * qu'on contourne.
 *
 * `aucunBareme` distingue « on cherche encore » de « rien ne couvre ce client ».
 * L'écran peut alors le dire au lieu d'afficher un champ vide sans explication.
 */
export function usePrixPropose({
  clientId,
  clientType,
  stationId,
  operationType,
  actif = true,
} = {}) {
  const [propose, setPropose] = useState(null);
  const [chargement, setChargement] = useState(false);

  useEffect(() => {
    if (!actif) return undefined;
    // Sans aucun critère, la résolution rendrait le barème général — utile
    // seulement si l'écran le demande explicitement par `operationType`.
    if (!clientId && !clientType && !stationId && !operationType) {
      setPropose(null);
      return undefined;
    }

    let abandonne = false;
    setChargement(true);
    pricingRulesAPI
      .resolve({ clientId, clientType, stationId, operationType })
      .then((resolu) => {
        if (abandonne) return;
        setPropose(resolu);
        setChargement(false);
      });

    return () => {
      abandonne = true;
    };
  }, [clientId, clientType, stationId, operationType, actif]);

  return {
    prix: propose?.price_per_liter ?? null,
    nomBareme: propose?.rule_name || null,
    devise: propose?.currency || null,
    chargement,
    aucunBareme: !chargement && propose === null,
  };
}

/**
 * Applique le prix proposé à un champ **tant que personne ne l'a touché**.
 *
 * Le garde est `toucheParLHumain`: une fois vrai, plus rien ne réécrit la
 * valeur. C'est la différence entre un champ pré-rempli et un champ qui se
 * bat contre son utilisateur.
 */
export function appliquerPrixPropose({ prix, valeurActuelle, toucheParLHumain }) {
  if (toucheParLHumain) return valeurActuelle;
  if (prix == null) return valeurActuelle;
  if (valeurActuelle !== '' && valeurActuelle != null) return valeurActuelle;
  return String(prix);
}

export default usePrixPropose;
